import React, { useMemo } from 'react';
import classNames from 'classnames';
import { css } from '@emotion/core';
import { DynamicTableStateless } from '@atlaskit/dynamic-table';
import { HeadCellType, LoadingSpinnerSizeType, RowType } from '@atlaskit/dynamic-table/dist/types/types';
import EmptyState from '@atlaskit/empty-state';
import { TableLoader } from '@css-xp/ppc-page-loaders';
import { ENABLE_DESIGN_ITERATION_1 } from '@css-xp/ppc-constants';
import { CustomPagination } from '../../custom-pagination';
import './dynamic-result-table.scss';
import { useFeatureToggle } from '../../hooks';

interface FormData {
  searchTerm: string;
}

const wrapperStyles = css({
  '& div:first-child': {
    // input style
    marginBottom: 0,
  },
});

export interface HeadCell extends HeadCellType {
  isMandatory?: boolean;
  isChecked?: boolean;
}

interface Props {
  headingText?: string;
  header?: React.ReactElement;
  beforeTable?: React.ReactElement | null;
  isLoading?: boolean;
  totalCount?: number;
  children?: React.ReactNode;
  loadingSpinnerSize?: LoadingSpinnerSizeType;
  head?: { cells: HeadCell[] };
  rows?: RowType[];
  // Number of rows per page
  rowsPerPage?: number;
  currentPage?: number;
  onPageChange?: (page: number) => unknown;
  onSearch?: (formdata: FormData) => Promise<void>;
  onRowChange?: (rowsPerPage: number) => unknown;
  emptyStateProps: React.ComponentProps<typeof EmptyState>;
  isPaginationEnabled?: boolean;
  // TODO :: remove this flag and use onSearch method instead as its redundant
  isSearchEnabled?: boolean;
  actions?: React.ElementType<any>[];
  testId?: string;
  className?: string;
  boxShadow?: boolean;
  error?: string;
  errorStateProps?: React.ComponentProps<typeof EmptyState>;
  footer?: React.ReactElement | null;
  customiseColumnView?: React.ReactElement;
  showFilters?: boolean;
  shouldFreeze?: boolean;
  alternateRowColor?: boolean;
  sortKey?: string;
  sortOrder?: 'ASC' | 'DESC';
  onSort?: (data?: any) => void;
  hideHeadOnEmptyState?: boolean;
  beforeTableHead?: React.ReactElement | null;
}

const _renderActions = (actions: React.ElementType<any>[]) => {
  return (
    <div className="actions-container" data-testid="actions-container">
      {actions.map((Action, index) => {
        return <Action key={`action-${index}`} />;
      })}
    </div>
  );
};

export const DynamicResultTable: React.FC<Props> = ({
  headingText,
  header,
  head,
  rowsPerPage,
  currentPage,
  children,
  totalCount,
  isLoading,
  rows,
  className,
  loadingSpinnerSize = 'large',
  onPageChange,
  isPaginationEnabled = false,
  onRowChange,
  emptyStateProps,
  actions,
  testId,
  boxShadow = true,
  errorStateProps,
  error,
  footer = null,
  customiseColumnView,
  sortKey,
  sortOrder,
  onSort,
  showFilters = false,
  shouldFreeze = false,
  beforeTable = null,
  alternateRowColor = true,
  hideHeadOnEmptyState,
  beforeTableHead,
}: Props) => {
  const [isEnabled] = useFeatureToggle();
  const showPagination = useMemo(() => {
    if (isPaginationEnabled && totalCount && rowsPerPage && totalCount > rowsPerPage) {
      return rowsPerPage;
    }
    return undefined;
  }, [isPaginationEnabled, rowsPerPage, totalCount]);
  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);

  // in case column freeze is there, we need our columns to scroll under Tab bar
  React.useEffect(() => {
    if (shouldFreeze) {
      const akTabs = document?.querySelector("div[role='tablist']")?.parentElement;
      if (akTabs) akTabs.style.zIndex = '2';
    }
  }, []);

  const shouldShowActions = actions && !!actions.length;

  return (
    <div className="dynamic-result-table" data-testid={'dynamic-result-table' + (testId ? `-${testId}` : '')}>
      {header ? header : headingText ? <h4>{headingText}</h4> : null}
      <div
        className={classNames('dynamic-result-table-wrapper', className, {
          'box-shadow': boxShadow && !isDesignIteration1Enabled,
          freeze: shouldFreeze,
          'alternate-row-color': alternateRowColor,
          'dynamic-result-table-wrapper-v2': isDesignIteration1Enabled,
        })}
      >
        {beforeTable}
        <div className="dynamic-result-table-header">
          {showFilters && children && (
            <div className="dynamic-result-table-filters" data-testid="dynamic-result-table-filters">
              {children}
            </div>
          )}
          {customiseColumnView ? <div className="customise-column-view">{customiseColumnView}</div> : null}
        </div>
        {shouldShowActions && _renderActions(actions)}
        {beforeTableHead && <div className="dynamic-result-table-before-table-head">{beforeTableHead}</div>}
        {/* There is a extra space coming if pagination is not there. wrapperStyles makes it dynamically*/}
        <div css={!isPaginationEnabled && wrapperStyles}>
          <DynamicTableStateless
            head={hideHeadOnEmptyState && !rows?.length ? { cells: [] } : head}
            rows={rows}
            emptyView={
              error && errorStateProps ? <EmptyState {...errorStateProps} /> : <EmptyState {...emptyStateProps} />
            }
            loadingSpinnerSize={loadingSpinnerSize}
            isLoading={isLoading}
            isFixedSize
            sortKey={sortKey}
            sortOrder={sortOrder}
            onSort={onSort}
            totalRows={totalCount}
            page={currentPage}
            rowsPerPage={showPagination}
            onSetPage={onPageChange}
            testId={'dynamic-table-stateless' + (testId ? `-${testId}` : '')}
          />
          {isLoading && isDesignIteration1Enabled && (
            // Kept after DynamicTableStateless to overlap the internal loader of DynamicTableStateless
            <div className="table-loader-wrapper">
              <TableLoader
                hideSearchBar={!showFilters}
                hideActions={!customiseColumnView && !shouldShowActions}
                hideFilters
              />
            </div>
          )}
        </div>
        {isPaginationEnabled && (
          <div
            className={classNames({
              'controlled-pagination-wrapper': !showPagination,
            })}
          >
            <CustomPagination
              rowCount={rowsPerPage}
              totalCount={totalCount}
              currentPage={currentPage}
              onChangeRow={onRowChange}
            />
          </div>
        )}
        {footer}
      </div>
    </div>
  );
};

export default DynamicResultTable;
